import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Joyride, EVENTS, STATUS, type EventData, type Step } from 'react-joyride';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import TourTooltip from './TourTooltip';
import TourCompletionDialog from './TourCompletionDialog';

/**
 * One-time onboarding tour for school admins. Uses react-joyride v3 in
 * controlled mode (we drive `stepIndex` ourselves) so we can pause between
 * steps that live on different routes, navigate, wait for the next target
 * to mount, then resume.
 *
 * Completion is persisted per-user in localStorage. A school admin sees the
 * tour at most once unless they explicitly relaunch from the profile menu
 * (the `relaunchWelcomeTour` helper dispatches `edubook:relaunch-tour`).
 */

const STORAGE_KEY_PREFIX = 'edubook.tour.completed.v1.';
const RELAUNCH_EVENT = 'edubook:relaunch-tour';

// Each step carries an optional `route` — when the tour reaches it, we
// navigate first, wait for the target to render, then show the tooltip.
interface TourStep extends Step {
  route?: string;
}

const STEPS: TourStep[] = [
  {
    target: 'body',
    placement: 'center',
    title: 'Welcome to Edubook',
    content:
      "A 30-second tour of the essentials. You can skip any time and relaunch from your profile menu later.",
  },
  {
    target: '[data-tour="sidebar"]',
    placement: 'right',
    title: 'The sidebar',
    content:
      'Every module lives here — Dashboard, Academic Sessions, Enrollments, Examinations, Reports, Settings, and more. The active section is highlighted in primary tone.',
  },
  {
    target: '[data-tour="topbar-profile"]',
    placement: 'left',
    title: 'Settings from your profile',
    content:
      'Your account menu also gets you to Settings, Billing, and lets you relaunch this tour any time. Click the profile button up here, then choose Settings.',
  },
  {
    route: '/app/settings',
    // Spotlight the Settings sub-rail (the inner categories panel) so the
    // user sees exactly what to click. Tooltip with placement='right' floats
    // over the content area on the right.
    target: '[data-tour="settings-rail"]',
    placement: 'right',
    title: 'Settings — set up your school',
    content:
      'Start here. Settings are grouped in the order a school sets itself up: School, Academics, Calendar, Staff, Students. Walk through it top to bottom — Regional preferences first, then Roles, Classes, Subjects and Grades.',
  },
  {
    route: '/app/settings/sessions',
    target: '[data-tour="main-content"]',
    placement: 'top',
    title: 'Academic Sessions',
    content:
      'Sessions are the foundation — every enrollment, exam, and report sits inside an active session. Create one for the current academic year before adding students.',
  },
  {
    route: '/app/enrollments',
    target: '[data-tour="main-content"]',
    placement: 'top',
    title: 'Enrollments — your students',
    content:
      "Add students individually or in bulk, transfer between classes, issue letters or fines, and open the student profile drawer from any row. You're all set — happy teaching!",
  },
];

const WAIT_FOR_TARGET_MS = 350;

const WelcomeTour: React.FC = () => {
  const theme = useTheme();
  const { user, decodedToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isSchoolAdmin = Boolean(decodedToken?.isSchoolAdmin);

  const storageKey = useMemo(() => {
    if (!user?._id) return null;
    return `${STORAGE_KEY_PREFIX}${user._id}`;
  }, [user?._id]);

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  // Celebratory dialog after a successful finish. Skipping the tour
  // (clicking "Skip tour" or the X) intentionally doesn't trigger it — the
  // user told us they don't want it right now.
  const [completionOpen, setCompletionOpen] = useState(false);

  // Strip the joyride-only `route` field before handing steps to the lib.
  const joyrideSteps: Step[] = useMemo(
    () => STEPS.map(({ route, ...rest }) => rest),
    []
  );

  // Auto-start once per school admin. Guard on storage key so we don't fire
  // before the user is loaded (would leave a flag against an anon key).
  useEffect(() => {
    if (!storageKey || !isSchoolAdmin) return;
    const done = window.localStorage.getItem(storageKey);
    if (done) return;
    // Delay 600ms so the layout settles after login and the sidebar/topbar
    // targets exist when joyride mounts.
    const t = window.setTimeout(() => {
      setStepIndex(0);
      setRun(true);
    }, 600);
    return () => window.clearTimeout(t);
  }, [storageKey, isSchoolAdmin]);

  // Manual relaunch from the profile menu.
  useEffect(() => {
    const handler = () => {
      setStepIndex(0);
      setRun(true);
    };
    window.addEventListener(RELAUNCH_EVENT, handler);
    return () => window.removeEventListener(RELAUNCH_EVENT, handler);
  }, []);

  const finish = useCallback((celebrate: boolean) => {
    setRun(false);
    setStepIndex(0);
    if (storageKey) window.localStorage.setItem(storageKey, new Date().toISOString());
    if (celebrate) setCompletionOpen(true);
  }, [storageKey]);

  // Controlled-mode handler. Joyride fires `step:after` whenever a step is
  // about to be replaced. We advance stepIndex ourselves; when the upcoming
  // step lives on a different route, we navigate first and resume after a
  // short delay so the target mounts.
  const handleEvent = useCallback((data: EventData) => {
    const { type, action, index, status } = data;

    if (status === STATUS.FINISHED) {
      finish(true);
      return;
    }
    if (status === STATUS.SKIPPED) {
      finish(false);
      return;
    }

    if (type === EVENTS.STEP_AFTER || type === EVENTS.ERROR) {
      const next = action === 'prev' ? index - 1 : index + 1;
      if (next < 0 || next >= STEPS.length) {
        // Reached the end via Next → celebrate. Hitting Back from step 0
        // is the only other path here and shouldn't celebrate, but joyride
        // doesn't surface that case (prev from 0 is clamped).
        finish(action !== 'prev');
        return;
      }
      const upcoming = STEPS[next];
      if (upcoming.route && upcoming.route !== location.pathname) {
        setRun(false);
        navigate(upcoming.route);
        window.setTimeout(() => {
          setStepIndex(next);
          setRun(true);
        }, WAIT_FOR_TARGET_MS);
      } else {
        setStepIndex(next);
      }
    }
  }, [finish, location.pathname, navigate]);

  if (!isSchoolAdmin) return null;

  return (
    <>
      <Joyride
        steps={joyrideSteps}
        run={run}
        stepIndex={stepIndex}
        continuous
        scrollToFirstStep
        onEvent={handleEvent}
        tooltipComponent={TourTooltip}
        locale={{ back: 'Back', next: 'Next', last: 'Done', skip: 'Skip tour' }}
        options={{
          // The custom TourTooltip handles all chrome; we just need joyride's
          // overlay, spotlight, and positioning logic. `skipBeacon` jumps
          // straight to the tooltip — no transient pulsing dot.
          skipBeacon: true,
          buttons: ['back', 'primary', 'skip'],
          // Matches the portal's primary tone.
          primaryColor: theme.palette.primary.main,
          overlayColor: 'rgba(15, 23, 42, 0.55)',
          zIndex: theme.zIndex.modal + 100,
        }}
      />
      <TourCompletionDialog
        open={completionOpen}
        onClose={() => setCompletionOpen(false)}
        onRelaunch={() => {
          setCompletionOpen(false);
          // Reset to first step and run again. Slight delay lets the
          // dialog close animation finish before the spotlight reopens.
          window.setTimeout(() => {
            setStepIndex(0);
            setRun(true);
          }, 200);
        }}
      />
    </>
  );
};

/**
 * Trigger the tour to restart. Call this from a menu item / button anywhere
 * in the tree — `WelcomeTour` listens for the event and resets to step 0.
 */
export const relaunchWelcomeTour = () => {
  window.dispatchEvent(new Event(RELAUNCH_EVENT));
};

export default WelcomeTour;
